import { acceptHMRUpdate, defineStore } from 'pinia'
import { LocalNotifications } from '@capacitor/local-notifications'
import type { LocalNotificationSchema } from '@capacitor/local-notifications'
import { Capacitor } from '@capacitor/core'
import { DateTime } from 'luxon'
import { i18n } from '@/locales/locales'
import { colors } from '@/theme'
import { useAuthStore } from './AuthStore'
import { useAbsenceStore } from './AbsenceStore'
import { report } from '@/classes/manager/ExceptionReportingManager'
import { useEmployeeStore } from './EmployeeStore'
import { ABSENCE_STATES } from '@/classes/Absence'
import { useEventStore } from './EventStore'
import { Task } from '@/classes/Task'

const { t } = i18n
export interface WeekdayReminder {
  day: string
  dayNumber: number
  enabled: boolean
  time: string
}

export interface WeekdayDurationReminder {
  day: string
  dayNumber: number
  enabled: boolean
  durationMinutes: number
}

type TReminderSettings = {
  isIndividual: boolean
  time: string | null
  weekdays: WeekdayReminder[]
}

type TDurationReminderSettings = {
  isIndividual: boolean
  durationMinutes: number | null
  weekdays: WeekdayDurationReminder[]
}

type TBreakReminder = {
  enabled: boolean
  triggerAfterMinutes: number
}

type TState = {
  clockinReminders: TReminderSettings
  clockoutReminders: TDurationReminderSettings
  _breakReminder: TBreakReminder
  permissionsGranted: boolean
}

export function createDefaultReminders(time: string): WeekdayReminder[] {
  return [
    { day: t('words.monday'), dayNumber: 2, enabled: false, time },
    { day: t('words.tuesday'), dayNumber: 3, enabled: false, time },
    { day: t('words.wednesday'), dayNumber: 4, enabled: false, time },
    { day: t('words.thursday'), dayNumber: 5, enabled: false, time },
    { day: t('words.friday'), dayNumber: 6, enabled: false, time },
    { day: t('words.saturday'), dayNumber: 7, enabled: false, time },
    { day: t('words.sunday'), dayNumber: 1, enabled: false, time },
  ]
}

export function createDefaultDurationReminders(
  durationMinutes: number,
): WeekdayDurationReminder[] {
  return [
    { day: t('words.monday'), dayNumber: 2, enabled: false, durationMinutes },
    { day: t('words.tuesday'), dayNumber: 3, enabled: false, durationMinutes },
    {
      day: t('words.wednesday'),
      dayNumber: 4,
      enabled: false,
      durationMinutes,
    },
    { day: t('words.thursday'), dayNumber: 5, enabled: false, durationMinutes },
    { day: t('words.friday'), dayNumber: 6, enabled: false, durationMinutes },
    { day: t('words.saturday'), dayNumber: 7, enabled: false, durationMinutes },
    { day: t('words.sunday'), dayNumber: 1, enabled: false, durationMinutes },
  ]
}

const initialState: TState = {
  clockinReminders: {
    isIndividual: false,
    time: null,
    weekdays: createDefaultReminders('09:00'),
  },
  clockoutReminders: {
    isIndividual: false,
    durationMinutes: null,
    weekdays: createDefaultDurationReminders(480), // 8 hours default
  },
  _breakReminder: {
    enabled: false,
    triggerAfterMinutes: 30,
  },
  permissionsGranted: false,
}

const NOTIFICATION_IDS = {
  CLOCKIN_BASE: 1000,
  CLOCKOUT_BASE: 2000,
  BREAK: 3000,
}

const REMINDER_CHANNEL_ID = 'clock-reminders-high-priority'

/**
 * Calculate the number of days since Unix epoch for a given date
 * Used to generate unique notification IDs based on the date
 */
function getDaysSinceEpoch(date: DateTime): number {
  return Math.floor(date.toMillis() / (1000 * 60 * 60 * 24))
}

/**
 * Convert Luxon weekday (1=Monday, 7=Sunday) to our dayNumber format (1=Sunday, 2=Monday, etc.)
 */
function luxonWeekdayToDayNumber(luxonWeekday: number): number {
  return (luxonWeekday % 7) + 1
}

export const useClockReminderStore = defineStore('clockReminderStore', {
  state: () => {
    return { ...initialState }
  },

  getters: {
    breakReminder(state): { enabled: boolean; triggerAfter: string } {
      return {
        enabled: state._breakReminder.enabled,
        triggerAfter: `${Math.floor(
          state._breakReminder.triggerAfterMinutes / 60,
        )
          .toString()
          .padStart(2, '0')}:${(state._breakReminder.triggerAfterMinutes % 60)
          .toString()
          .padStart(2, '0')}`,
      }
    },
    clockoutReminder(state): {
      enabled: boolean
      triggerAfter: string
      isIndividual: boolean
    } {
      const durationMinutes =
        state.clockoutReminders.durationMinutes ||
        state.clockoutReminders.weekdays[0]?.durationMinutes ||
        480
      return {
        enabled:
          state.clockoutReminders.durationMinutes !== null ||
          state.clockoutReminders.weekdays.some((w) => w.enabled),
        triggerAfter: `${Math.floor(durationMinutes / 60)
          .toString()
          .padStart(2, '0')}:${(durationMinutes % 60)
          .toString()
          .padStart(2, '0')}`,
        isIndividual: state.clockoutReminders.isIndividual,
      }
    },
    hasAnyRemindersEnabled(state): boolean {
      const hasClockinReminders =
        state.clockinReminders.time !== null ||
        state.clockinReminders.weekdays.some((w) => w.enabled)

      const hasClockoutReminders =
        state.clockoutReminders.durationMinutes !== null ||
        state.clockoutReminders.weekdays.some((w) => w.enabled)

      const hasBreakReminders = state._breakReminder.enabled

      return hasClockinReminders || hasClockoutReminders || hasBreakReminders
    },
  },

  actions: {
    async createHighPriorityChannel() {
      // Only needed for Android
      if (Capacitor.getPlatform() !== 'android') return

      try {
        const currentChannels = await LocalNotifications.listChannels()
        if (
          currentChannels.channels.some(
            (channel) => channel.id === REMINDER_CHANNEL_ID,
          )
        ) {
          console.log(
            'ClockReminderStore: channel already exists, skipping creation',
          )
          return
        }

        await LocalNotifications.createChannel({
          id: REMINDER_CHANNEL_ID,
          name: t('phrases.clock_reminder_channel_name'),
          description: t('phrases.clock_reminder_channel_description'),
          importance: 5, // IMPORTANCE_HIGH - Shows as heads-up notification
          visibility: 1, // VISIBILITY_PUBLIC - Shows on lock screen
          vibration: true, // Enable vibration
          sound: 'notificationsound.wav', // Use the configured sound
          lights: true, // Enable LED lights if available
          lightColor: colors.clockin,
        })
      } catch (error) {
        console.error('Error creating notification channel:', error)
      }
    },

    async requestPermissions() {
      if (Capacitor.getPlatform() === 'web') {
        console.log('Permissions not applicable on web platform')
        return false
      }

      try {
        const permission = await LocalNotifications.checkPermissions()

        if (permission.display === 'granted') {
          this.permissionsGranted = true
          return true
        }

        if (permission.display === 'prompt') {
          console.debug(
            'Requesting push notification permission in ClockReminderStore',
          )
          const result = await LocalNotifications.requestPermissions()
          this.permissionsGranted = result.display === 'granted'
          return this.permissionsGranted
        }

        this.permissionsGranted = false
        return false
      } catch (error) {
        console.error('Error requesting notification permissions:', error)
        this.permissionsGranted = false
        return false
      }
    },

    async saveClockinReminders(
      isIndividual: boolean,
      time: string | null,
      weekdays: WeekdayReminder[],
    ) {
      this.clockinReminders = {
        isIndividual,
        time,
        weekdays: [...weekdays],
      }
      await this.scheduleClockReminderNotifications()
    },

    async saveClockoutReminders(
      isIndividual: boolean,
      durationMinutes: number | null,
      weekdays: WeekdayDurationReminder[],
    ) {
      this.clockoutReminders = {
        isIndividual,
        durationMinutes,
        weekdays: [...weekdays],
      }
      // Clockout reminders are now scheduled dynamically on clock-in
      // So we only need to schedule clockin reminders here
      await this.scheduleClockReminderNotifications()
    },

    saveBreakReminder(enabled: boolean, triggerAfter: string) {
      this._breakReminder = {
        enabled,
        triggerAfterMinutes:
          parseInt(triggerAfter.split(':')[0], 10) * 60 +
          parseInt(triggerAfter.split(':')[1], 10),
      }
    },

    async scheduleBreakReminder() {
      if (this._breakReminder.enabled === false) {
        return
      }
      if (Capacitor.getPlatform() === 'web') {
        console.log('Notifications not supported on web platform')
        return
      }
      if (!useAuthStore().isPersonalDevice) {
        report(
          new Error('Tried to schedule break reminder on non personal device'),
        )
        return
      }

      const { employee } = useEmployeeStore()
      if (!employee?.can_use_break_reminder) {
        report(
          new Error(
            'Tried to schedule break reminder for employee without permission',
          ),
        )
        return
      }

      // Request permissions if not already granted
      const hasPermissions = await this.requestPermissions()
      if (!hasPermissions) {
        console.warn('Notification permissions not granted for break reminder')
        return
      }

      await this.createHighPriorityChannel()

      await LocalNotifications.schedule({
        notifications: [
          {
            id: NOTIFICATION_IDS.BREAK,
            title: t('phrases.break_reminder_title'),
            body: t('phrases.break_reminder'),
            channelId: REMINDER_CHANNEL_ID,
            sound: 'notificationsound.wav',
            smallIcon: 'clockin.png',
            iconColor: colors.clockin,
            actionTypeId: 'BREAK_REMINDER',
            extra: {
              type: 'break',
            },
            schedule: {
              at: new Date(
                Date.now() + this._breakReminder.triggerAfterMinutes * 60000,
              ),
              allowWhileIdle: true,
            },
          },
        ],
      })
    },

    async scheduleClockoutReminder(currentWorkDurationMinutes = 0) {
      if (Capacitor.getPlatform() === 'web') {
        console.log('Notifications not supported on web platform')
        return
      }
      if (!useAuthStore().isPersonalDevice) {
        report(
          new Error(
            'Tried to schedule clockout reminder on non personal device',
          ),
        )
        return
      }

      const { employee } = useEmployeeStore()
      if (!employee?.can_use_clockout_reminder) {
        report(
          new Error(
            'Tried to schedule clockout reminder for employee without permission',
          ),
        )
        return
      }

      // Determine the target duration based on current weekday
      const today = DateTime.now()
      const dayNumber = luxonWeekdayToDayNumber(today.weekday)

      let targetDurationMinutes: number
      if (this.clockoutReminders.isIndividual) {
        const weekday = this.clockoutReminders.weekdays.find(
          (w) => w.dayNumber === dayNumber && w.enabled,
        )
        if (!weekday) {
          // No reminder enabled for today
          return
        }
        targetDurationMinutes = weekday.durationMinutes
      } else {
        if (this.clockoutReminders.durationMinutes === null) {
          // No general reminder set
          return
        }
        targetDurationMinutes = this.clockoutReminders.durationMinutes
      }

      // Calculate remaining work time needed (target - current work done)
      const remainingMinutes =
        targetDurationMinutes - currentWorkDurationMinutes

      // If already exceeded target or remaining time is too short, don't schedule
      if (remainingMinutes <= 0) {
        console.log(
          `Target work duration already reached (${currentWorkDurationMinutes}/${targetDurationMinutes} minutes)`,
        )
        await this.cancelClockOutReminderForToday()
        return
      }

      // Request permissions if not already granted
      const hasPermissions = await this.requestPermissions()
      if (!hasPermissions) {
        console.warn(
          'Notification permissions not granted for clockout reminder',
        )
        return
      }

      await this.createHighPriorityChannel()

      // Cancel any existing clockout reminder before scheduling a new one
      await this.cancelClockOutReminderForToday()

      console.log(
        `Scheduling clockout reminder in ${remainingMinutes} minutes (current: ${currentWorkDurationMinutes}, target: ${targetDurationMinutes})`,
      )

      await LocalNotifications.schedule({
        notifications: [
          {
            id:
              NOTIFICATION_IDS.CLOCKOUT_BASE +
              getDaysSinceEpoch(today.startOf('day')),
            title: t('phrases.clockout_reminder_title'),
            body: t('phrases.clockout_reminder'),
            channelId: REMINDER_CHANNEL_ID,
            sound: 'notificationsound.wav',
            smallIcon: 'clockin.png',
            iconColor: '#1a9dd9',
            actionTypeId: 'CLOCK_OUT_REMINDER',
            extra: {
              type: 'clockout',
              date: today.toISODate(),
            },
            schedule: {
              at: new Date(Date.now() + remainingMinutes * 60000),
              allowWhileIdle: true,
            },
          },
        ],
      })
    },

    scheduleClockinReminderNotifications(): LocalNotificationSchema[] {
      const notifications: LocalNotificationSchema[] = []
      const absenceStore = useAbsenceStore()
      const eventStore = useEventStore()

      // Generate dates for the next 2 weeks
      const today = DateTime.now()
      const dates: DateTime[] = []
      for (let i = 0; i < 14; i++) {
        dates.push(today.plus({ days: i }).startOf('day'))
      }

      // Build clock-in notifications
      for (const date of dates) {
        // Skip if user has a granted absence on this date
        if (absenceStore.isAbsentOnDate(date, [ABSENCE_STATES.GRANTED])) {
          continue
        }

        // Skip today's clock-in reminder if user has already clocked in
        const isToday = date.hasSame(DateTime.now(), 'day')
        if (isToday) {
          const todaysFirstEvent = eventStore.todaysFirstEvent()
          if (
            todaysFirstEvent &&
            todaysFirstEvent.task_id !== Task.id.CLOCKOUT
          ) {
            continue
          }
        }

        const dayNumber = luxonWeekdayToDayNumber(date.weekday)

        if (this.clockinReminders.isIndividual) {
          // Individual reminders for each enabled weekday
          const weekday = this.clockinReminders.weekdays.find(
            (w) => w.dayNumber === dayNumber,
          )
          if (weekday && weekday.enabled) {
            const [hour, minute] = weekday.time.split(':').map(Number)
            const notificationDate = date.set({
              hour,
              minute,
              second: 0,
              millisecond: 0,
            })

            // Only schedule if in the future
            if (notificationDate > DateTime.now()) {
              notifications.push({
                id: NOTIFICATION_IDS.CLOCKIN_BASE + getDaysSinceEpoch(date),
                title: t('phrases.clockin_reminder_title'),
                body: t('phrases.clockin_reminder'),
                channelId: REMINDER_CHANNEL_ID,
                sound: 'notificationsound.wav',
                smallIcon: 'clockin.png',
                iconColor: colors.clockin,
                actionTypeId: 'CLOCK_IN_REMINDER',
                extra: {
                  type: 'clockin',
                  date: date.toISODate(),
                },
                schedule: {
                  at: notificationDate.toJSDate(),
                  allowWhileIdle: true,
                },
              })
            }
          }
        } else if (this.clockinReminders.time) {
          // Schedule for Monday-Friday only (dayNumber 2-6)
          if (dayNumber >= 2 && dayNumber <= 6) {
            const [hour, minute] = this.clockinReminders.time
              .split(':')
              .map(Number)
            const notificationDate = date.set({
              hour,
              minute,
              second: 0,
              millisecond: 0,
            })

            // Only schedule if in the future
            if (notificationDate > DateTime.now()) {
              notifications.push({
                id: NOTIFICATION_IDS.CLOCKIN_BASE + getDaysSinceEpoch(date),
                title: t('phrases.clockin_reminder_title'),
                body: t('phrases.clockin_reminder'),
                channelId: REMINDER_CHANNEL_ID,
                sound: 'notificationsound.wav',
                smallIcon: 'clockin.png',
                iconColor: colors.clockin,
                actionTypeId: 'CLOCK_IN_REMINDER',
                extra: {
                  type: 'clockin',
                  date: date.toISODate(),
                },
                schedule: {
                  at: notificationDate.toJSDate(),
                  allowWhileIdle: true,
                },
              })
            }
          }
        }
      }

      return notifications
    },

    async scheduleClockReminderNotifications() {
      if (Capacitor.getPlatform() === 'web') {
        console.log('Notifications not supported on web platform')
        return
      }
      if (!useAuthStore().isPersonalDevice) {
        return
      }

      const { employee } = useEmployeeStore()
      if (!employee) {
        report(new Error('No employee found for scheduling clock reminders'))
        return
      }

      try {
        console.log('dbg: hasAnyRemindersEnabled:', this.hasAnyRemindersEnabled)
        if (!this.hasAnyRemindersEnabled) {
          console.log('No reminders enabled, skipping permission request')
          await this.cancelAllNotifications()
          return
        }

        const hasPermissions = await this.requestPermissions()
        if (!hasPermissions) {
          console.warn('Notification permissions not granted')
          return
        }
        await this.createHighPriorityChannel()
        await this.cancelAllNotifications()

        const clockinNotifications = employee.can_use_clockin_reminder
          ? this.scheduleClockinReminderNotifications()
          : []

        // Clockout reminders are now scheduled dynamically on clock-in
        const notifications = [...clockinNotifications]

        if (notifications.length > 0) {
          await LocalNotifications.schedule({ notifications })
          console.log(
            `Scheduled ${notifications.length} notifications for the next 2 weeks`,
          )
        } else {
          console.log('No notifications to schedule')
        }
      } catch (error) {
        console.error('Error scheduling notifications:', error)
        throw error
      }
    },

    async cancelClockInReminderForToday() {
      if (Capacitor.getPlatform() === 'web') return
      if (!useAuthStore().isPersonalDevice) return

      try {
        const today = DateTime.now().startOf('day')
        const notificationId =
          NOTIFICATION_IDS.CLOCKIN_BASE + getDaysSinceEpoch(today)

        await LocalNotifications.cancel({
          notifications: [{ id: notificationId }],
        })
        console.log(
          `Canceled clock-in reminder for today (ID: ${notificationId})`,
        )
      } catch (error) {
        console.error('Error canceling clock-in reminder:', error)
      }
    },

    async cancelClockOutReminderForToday() {
      if (Capacitor.getPlatform() === 'web') return
      if (!useAuthStore().isPersonalDevice) return

      try {
        const today = DateTime.now().startOf('day')
        const notificationId =
          NOTIFICATION_IDS.CLOCKOUT_BASE + getDaysSinceEpoch(today)

        await LocalNotifications.cancel({
          notifications: [{ id: notificationId }],
        })
        console.log(
          `Canceled clock-out reminder for today (ID: ${notificationId})`,
        )
      } catch (error) {
        console.error('Error canceling clock-out reminder:', error)
      }
    },

    async cancelClockinReminders() {
      try {
        const pending = await LocalNotifications.getPending()

        // Days since epoch should be reasonably between ~1997 and ~2106
        const MIN_DAYS = 10000
        const MAX_DAYS = 50000

        const clockinIds = pending.notifications
          .filter((n) => {
            const daysOffset = n.id - NOTIFICATION_IDS.CLOCKIN_BASE
            return daysOffset >= MIN_DAYS && daysOffset <= MAX_DAYS
          })
          .map((n) => ({ id: n.id }))

        if (clockinIds.length > 0) {
          await LocalNotifications.cancel({ notifications: clockinIds })
          console.log(`Canceled ${clockinIds.length} clock-in reminders`)
        }
      } catch (error) {
        console.error('Error canceling clock-in reminders:', error)
      }
    },

    async cancelClockoutReminders() {
      try {
        const pending = await LocalNotifications.getPending()

        // Days since epoch should be reasonably between ~1997 and ~2106
        const MIN_DAYS = 10000
        const MAX_DAYS = 50000

        const clockoutIds = pending.notifications
          .filter((n) => {
            const daysOffset = n.id - NOTIFICATION_IDS.CLOCKOUT_BASE
            return daysOffset >= MIN_DAYS && daysOffset <= MAX_DAYS
          })
          .map((n) => ({ id: n.id }))

        if (clockoutIds.length > 0) {
          await LocalNotifications.cancel({ notifications: clockoutIds })
          console.log(`Canceled ${clockoutIds.length} clock-out reminders`)
        }
      } catch (error) {
        console.error('Error canceling clock-out reminders:', error)
      }
    },

    async cancelBreakReminders() {
      try {
        await LocalNotifications.cancel({
          notifications: [{ id: NOTIFICATION_IDS.BREAK }],
        })
        console.log('Canceled break reminder')
      } catch (error) {
        console.error('Error canceling break reminder:', error)
      }
    },

    async cancelAllNotifications() {
      await this.cancelClockinReminders()
      await this.cancelClockoutReminders()
      await this.cancelBreakReminders()
    },

    async sync() {
      // Clock reminders currently don't need server sync, but we are using this
      // to make sure notifications are always correctly scheduled
      await this.scheduleClockReminderNotifications()
    },

    hydrate(state: string) {
      console.log('Hydrating ClockReminderStore from state:', state)
      this.$state = JSON.parse(state)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useClockReminderStore, import.meta.hot),
  )
}
